import { order, sanitize } from "./generate";
import type { Afb, Question } from "./types";
import type { ImportedSection } from "@/services/path.service";

type Obj = Record<string, unknown>;

const pick = (o: Obj, ...keys: string[]): unknown => keys.map((k) => o[k]).find((v) => v !== undefined);
const list = (v: unknown): Obj[] => (Array.isArray(v) ? v.filter((x): x is Obj => !!x && typeof x === "object") : []);

function extractJson(text: string): unknown {
  const fenced = /```(?:json)?\s*([\s\S]*?)```/i.exec(text)?.[1];
  const raw = fenced ?? text;
  const start = raw.indexOf("{");
  const end = raw.lastIndexOf("}");
  if (start < 0 || end <= start) throw new Error("Kein JSON gefunden – bitte die komplette Antwort der KI einfügen.");
  try {
    return JSON.parse(raw.slice(start, end + 1));
  } catch (err) {
    throw new Error(`JSON ist unvollständig oder fehlerhaft (${(err as Error).message}). Evtl. wurde die Antwort abgeschnitten – die KI um "weiter" bitten.`);
  }
}

/**
 * `allowEmpty`: Schritt-1-Import (nur Gliederung) legt Lektionen bewusst
 * OHNE Aufgaben an – die kommen erst mit dem Schritt-2-Import je Abschnitt.
 * Ohne das würde jede Lektion ohne "aufgaben" sofort wieder rausfallen.
 */
export function parsePathImport(
  text: string,
  allowEmpty = false
): { sections: ImportedSection[]; questions: number; dropped: number } {
  const data = extractJson(text) as Obj;
  const noRefs = new Set<string>();
  const noLabels = new Map<string, string>();
  let questions = 0;
  let dropped = 0;

  const sections: ImportedSection[] = list(pick(data, "abschnitte", "sections", "sequenzen")).map((sec, si) => ({
    name: String(pick(sec, "name", "titel", "title") ?? `Abschnitt ${si + 1}`).trim() || `Abschnitt ${si + 1}`,
    lessons: list(pick(sec, "lektionen", "lessons")).map((les, li) => {
      const raw = list(pick(les, "aufgaben", "questions", "items"));
      const clean = raw
        .map((q) => sanitize(q, noRefs, noLabels))
        .filter((q): q is Question => !!q);
      questions += clean.length;
      dropped += raw.length - clean.length;
      const afb = Number(pick(les, "afb"));
      return {
        title: String(pick(les, "titel", "title") ?? `Lektion ${li + 1}`).trim() || `Lektion ${li + 1}`,
        goal: String(pick(les, "ziel", "goal") ?? "").trim(),
        icon: String(pick(les, "icon") ?? "book"),
        afb: (afb === 2 || afb === 3 ? afb : 1) as Afb,
        questions: order(clean),
      };
    }).filter((l) => allowEmpty || l.questions.length > 0),
  })).filter((s) => s.lessons.length > 0);

  if (!sections.length) {
    throw new Error(
      allowEmpty
        ? "Keine gültigen Abschnitte/Lektionen gefunden."
        : "Keine gültigen Abschnitte/Lektionen mit Aufgaben gefunden."
    );
  }
  return { sections, questions, dropped };
}
